import { useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import { IconFileText, IconPaperclip, IconPhoto, IconUpload, IconVideo, IconX } from '@tabler/icons-react';
import {
  Accordion,
  Badge,
  Button,
  Divider,
  EmptyState,
  IconButton,
  InlineAlert,
  InputTextArea,
  List,
  Metric,
  Panel,
  Progress,
  cn,
  formatDate,
  formatNumber,
  useToast,
  type AccordionItemData,
  type ListItemData,
} from 'escrowbuild-ui';

import {
  useDocuments,
  useEvidence,
  useFixtureVocabulary,
  useScheduleOf,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import { type DocumentRecord, type EvidenceItem, type Project } from '@/data/domain';

const TYPE_LABEL: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

const VERIFICATION_LABEL: Record<DocumentRecord['status'], AppTranslationKey> = {
  verified: 'status.verification.verified',
  'in-review': 'status.verification.inReview',
  rejected: 'status.verification.rejected',
};

const VERIFICATION_TONE = {
  verified: 'success',
  'in-review': 'info',
  rejected: 'error',
} as const;

const STATUS_TONE = {
  completed: 'success',
  'in-progress': 'info',
  planned: 'neutral',
} as const;

const STATUS_LABEL: Record<'completed' | 'in-progress' | 'planned', AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

interface StagedFile {
  id: string;
  name: string;
  kind: EvidenceItem['kind'];
  size: number;
  phaseId: string;
  caption: string;
}

/**
 * The domain has three evidence kinds and the file itself carries none of them,
 * so the kind is read off the MIME type: an image is a photo, a video is a video,
 * anything else is filed as a note until the document register takes it.
 */
function kindOf(file: File): EvidenceItem['kind'] {
  if (file.type.startsWith('image/')) return 'photo';
  if (file.type.startsWith('video/')) return 'video';
  return 'note';
}

const KIND_LABEL: Record<EvidenceItem['kind'], AppTranslationKey> = {
  photo: 'status.evidenceKind.photo',
  video: 'status.evidenceKind.video',
  note: 'status.evidenceKind.note',
};

const KIND_ICON = {
  photo: IconPhoto,
  video: IconVideo,
  note: IconPaperclip,
} as const;

function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) return `${formatNumber(bytes / 1_048_576, { precision: 1 })} MB`;
  return `${formatNumber(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

export function ProjectEvidencePage() {
  const t = useT();
  const { project } = useOutletContext<{ project: Project }>();
  const { toast } = useToast();

  /*
     The schedule, the photographs and the files this development holds, resolved
     for the active locale: a phase's name, a caption and a document's name are
     all text a reader reads, while the dates, sizes and flags beside them are
     not.
  */
  const phases = useScheduleOf(project.id);
  const projectEvidence = useEvidence(project.id);
  const projectDocuments = useDocuments(project.id);
  const vocabulary = useFixtureVocabulary();

  const currentPhase = phases.find((phase) => phase.status === 'in-progress') ?? phases[0];

  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [pending, setPending] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [uploadPhase, setUploadPhase] = useState(() => currentPhase?.id ?? '');
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState<string[]>(() => (currentPhase ? [currentPhase.id] : []));

  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLDivElement>(null);

  const released = projectEvidence.filter((item) => item.published).length;
  const awaiting = projectEvidence.length - released;
  const unreleasedDocuments = projectDocuments.filter((document) => !document.published);

  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    setPending((current) => [...current, ...files]);
  };

  const clearPending = () => {
    setPending([]);
    setNote('');
    if (inputRef.current) inputRef.current.value = '';
  };

  /*
     ponytail: the upload stops at the browser — a real one POSTs to the evidence
     endpoint and keeps the ids it returns. The staged record is the shape that
     response would have, so this one function is the whole upgrade.
  */
  const stage = () => {
    const phaseName = vocabulary.phase(uploadPhase);
    const caption = note.trim();
    const additions: StagedFile[] = pending.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      kind: kindOf(file),
      size: file.size,
      phaseId: uploadPhase,
      caption: caption || file.name,
    }));

    setStaged((current) => [...current, ...additions]);
    clearPending();
    setOpen((current) => (current.includes(uploadPhase) ? current : [...current, uploadPhase]));
    toast({
      variant: 'success',
      title: t('project.evidence.toast.staged', { count: additions.length }),
      description: t('project.evidence.toast.stagedBody', { phase: phaseName }),
    });
  };

  const attachToPhase = (phaseId: string) => {
    setUploadPhase(phaseId);
    uploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const items: AccordionItemData[] = phases.map((phase) => {
    const evidence = projectEvidence.filter((item) => item.phaseId === phase.id);
    const documents = projectDocuments.filter((document) => document.phaseId === phase.id);
    const phaseStaged = staged.filter((item) => item.phaseId === phase.id);
    const phaseReleased = evidence.filter((item) => item.published).length;
    const percent = evidence.length === 0 ? 0 : (phaseReleased / evidence.length) * 100;
    const empty = evidence.length === 0 && documents.length === 0 && phaseStaged.length === 0;

    const evidenceItems: ListItemData[] = evidence.map((item) => ({
      id: item.id,
      title: item.caption,
      subtitle: `${formatDate(item.date, { style: 'medium' })} · ${t('project.evidence.capturedBy', {
        name: item.capturedBy,
      })}`,
      icon: <IconPhoto size={16} aria-hidden="true" />,
      trailing: (
        <Badge tone={item.published ? 'success' : 'warning'} variant="subtle" size="sm">
          {item.published ? t('project.evidence.released') : t('project.evidence.notReleased')}
        </Badge>
      ),
    }));

    const documentItems: ListItemData[] = documents.map((document) => ({
      id: document.id,
      title: document.name,
      subtitle: `${t(TYPE_LABEL[document.type])} · ${formatDate(document.date, { style: 'medium' })} · ${document.size} · ${document.uploadedBy}`,
      icon: <IconFileText size={16} aria-hidden="true" />,
      trailing: (
        <span className="flex flex-wrap items-center justify-end gap-1.5">
          <Badge tone={VERIFICATION_TONE[document.status]} variant="subtle" size="sm">
            {t(VERIFICATION_LABEL[document.status])}
          </Badge>
          <Badge tone={document.published ? 'success' : 'warning'} variant="subtle" size="sm">
            {document.published ? t('project.evidence.released') : t('project.evidence.notReleased')}
          </Badge>
        </span>
      ),
    }));

    const stagedItems: ListItemData[] = phaseStaged.map((item) => {
      const Icon = KIND_ICON[item.kind];
      return {
        id: item.id,
        title: item.caption,
        subtitle: `${formatBytes(item.size)} · ${t(KIND_LABEL[item.kind])} · ${t('project.evidence.stagedNotUploaded')}`,
        icon: <Icon size={16} aria-hidden="true" />,
        trailing: (
          <IconButton
            label={t('project.evidence.removeStaged', { name: item.name })}
            icon={<IconX size={14} />}
            size="sm"
            variant="ghost"
            onClick={() => setStaged((current) => current.filter((entry) => entry.id !== item.id))}
          />
        ),
      };
    });

    return {
      id: phase.id,
      header: (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{phase.name}</span>
          <Badge tone={STATUS_TONE[phase.status]} variant="subtle" size="sm">
            {t(STATUS_LABEL[phase.status])}
          </Badge>
          <span className="text-xs font-normal text-muted">
            {t('project.evidence.phase.released', {
              released: formatNumber(phaseReleased),
              total: formatNumber(evidence.length),
            })}{' '}
            · {t('project.evidence.phase.documents', { count: documents.length })}
            {phaseStaged.length > 0
              ? ` · ${t('project.evidence.phase.staged', { count: phaseStaged.length })}`
              : ''}
          </span>
        </span>
      ),
      content: (
        <div className="flex flex-col gap-4">
          {empty ? (
            <EmptyState
              variant="cards"
              icon={<IconPhoto size={28} aria-hidden="true" />}
              title={t('project.evidence.empty.title')}
              description={t('project.evidence.empty.description')}
              action={
                <Button variant="outline" size="sm" onClick={() => attachToPhase(phase.id)}>
                  {t('project.evidence.attachFiles')}
                </Button>
              }
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-4">
                <div className="min-w-[16rem] flex-1">
                  <Progress
                    value={percent}
                    tone={percent === 100 ? 'success' : percent === 0 ? 'warning' : 'primary'}
                    size="sm"
                    label={t('project.evidence.progress', { phase: phase.name })}
                    showValue
                  />
                </div>
                <p className="text-xs text-body">
                  {t('project.evidence.phase.counts', {
                    captured: formatNumber(evidence.length),
                    released: formatNumber(phaseReleased),
                    awaiting: formatNumber(evidence.length - phaseReleased),
                  })}
                </p>
              </div>

              {evidenceItems.length > 0 ? (
                <div>
                  <h4 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                    {t('project.evidence.photosAndNotes')}
                  </h4>
                  <List
                    items={evidenceItems}
                    size="sm"
                    divided
                    label={t('project.evidence.list.evidence', { phase: phase.name })}
                  />
                </div>
              ) : null}

              {documentItems.length > 0 ? (
                <div>
                  <h4 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                    {t('project.evidence.documentsHeading')}
                  </h4>
                  <List
                    items={documentItems}
                    size="sm"
                    divided
                    label={t('project.evidence.list.documents', { phase: phase.name })}
                  />
                </div>
              ) : null}

              {stagedItems.length > 0 ? (
                <div>
                  <h4 className="font-display text-2xs font-semibold uppercase tracking-label text-warning">
                    {t('project.evidence.stagedHeading')}
                  </h4>
                  <List
                    items={stagedItems}
                    size="sm"
                    divided
                    label={t('project.evidence.list.staged', { phase: phase.name })}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('project.evidence.metric.captured')}
          value={projectEvidence.length}
          format="number"
        />
        <Metric
          label={t('project.evidence.metric.released')}
          value={released}
          format="number"
          tone="success"
          hint={t('project.evidence.metric.releasedHint')}
        />
        <Metric
          label={t('project.evidence.metric.awaiting')}
          value={awaiting}
          format="number"
          tone={awaiting > 0 ? 'warning' : 'success'}
          hint={t('project.evidence.metric.awaitingHint')}
        />
        <Metric
          label={t('project.evidence.metric.documents')}
          value={projectDocuments.length}
          format="number"
          hint={t('project.evidence.metric.documentsHint', {
            unreleased: unreleasedDocuments.length,
          })}
        />
      </div>

      <div ref={uploadRef}>
        <Panel
          title={t('project.evidence.upload.title')}
          description={t('project.evidence.upload.description')}
          padding="lg"
        >
          <div className="flex flex-col gap-4">
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(Array.from(event.dataTransfer.files));
              }}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xs border border-dashed px-4 py-6 text-center',
                dragging ? 'border-primary bg-primary-subtle' : 'border-line-strong bg-surface-muted',
              )}
            >
              <IconUpload size={24} aria-hidden="true" className="text-muted" />
              <p className="text-sm text-body">{t('project.evidence.upload.drop')}</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
                onChange={(event) => {
                  addFiles(Array.from(event.target.files ?? []));
                  event.target.value = '';
                }}
              />
              <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                {t('project.evidence.upload.choose')}
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('project.evidence.upload.attachTo')}
              </span>
              <div className="flex flex-wrap gap-2">
                {phases.map((phase) => (
                  <Badge
                    key={phase.id}
                    selectable
                    selected={uploadPhase === phase.id}
                    onSelectedChange={() => setUploadPhase(phase.id)}
                  >
                    {phase.name}
                  </Badge>
                ))}
              </div>
            </div>

            <InputTextArea
              id="evidence-upload-note"
              label={t('project.evidence.upload.captionLabel')}
              description={t('project.evidence.upload.captionDescription')}
              value={note}
              onChange={setNote}
              rows={2}
              maxLength={180}
            />

            {pending.length > 0 ? (
              <div className="flex flex-col gap-2">
                <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                  {t('project.evidence.upload.ready')}
                </h3>
                <ul className="flex list-none flex-wrap gap-2 p-0">
                  {pending.map((file, index) => {
                    const Icon = KIND_ICON[kindOf(file)];
                    return (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 rounded-xs border border-line bg-surface px-2 py-1 text-xs text-body"
                      >
                        <Icon size={14} aria-hidden="true" className="text-muted" />
                        <span className="max-w-[14rem] truncate">{file.name}</span>
                        <span className="text-muted">{formatBytes(file.size)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <Divider />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p role="status" className="text-xs text-body">
                {pending.length === 0
                  ? t('project.evidence.nothingStaged')
                  : t('project.evidence.readyFor', {
                      count: pending.length,
                      phase: vocabulary.phase(uploadPhase),
                    })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" disabled={pending.length === 0} onClick={clearPending}>
                  {t('project.evidence.clear')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={pending.length === 0}
                  onClick={stage}
                  leadingIcon={<IconUpload size={14} aria-hidden="true" />}
                >
                  {t('project.evidence.stage', { count: pending.length })}
                </Button>
              </div>
            </div>

            <InlineAlert
              tone="info"
              title={t('project.evidence.staging.title')}
              description={t('project.evidence.staging.description')}
            />
          </div>
        </Panel>
      </div>

      <Panel
        title={t('project.evidence.byPhase.title')}
        description={t('project.evidence.byPhase.description')}
        padding="lg"
      >
        <Accordion
          items={items}
          type="multiple"
          variant="separated"
          headingLevel={3}
          open={open}
          onOpenChange={setOpen}
        />
      </Panel>
    </div>
  );
}
